import * as THREE from 'three';
import { SHIP_CLASSES } from '@deepwake/shared';
import { buildShip, buildStation, buildGate, buildBeltBeacon, buildPlanet, buildWreck, buildAsteroid } from './models.js';
import { paintSkybox } from './textures.js';

/**
 * Model gallery (dev): /?gallery=ships|stations|misc renders every model in
 * a slowly turning row so a human (or a headless screenshot) can inspect the
 * detail pass without logging in.
 */
export function runGallery(kind: string): void {
  document.getElementById('login')?.classList.add('hidden');
  document.getElementById('hud')?.classList.add('hidden');
  const canvas = document.getElementById('view') as HTMLCanvasElement;
  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setSize(innerWidth, innerHeight);
  const scene = new THREE.Scene();
  scene.background = paintSkybox(0xd33b ^ 100);
  scene.add(new THREE.AmbientLight(0x8899bb, 0.7));
  const sun = new THREE.DirectionalLight(0xfff0d8, 1.7);
  sun.position.set(0.5, 0.8, 0.6);
  scene.add(sun);

  const camera = new THREE.PerspectiveCamera(55, innerWidth / innerHeight, 0.5, 1e7);
  const items: { obj: THREE.Group; label: string }[] = [];

  if (kind === 'stations') {
    const factions = ['Provenance Combine', 'Tidemark Compact', 'The Vigil', 'Unbound'];
    factions.forEach((f, i) => items.push({ obj: buildStation(f), label: f }));
    layout(items, 5200);
    camera.position.set(0, 2800, 11500);
  } else if (kind === 'misc') {
    items.push({ obj: buildGate(), label: 'Warden gate' });
    items.push({ obj: buildBeltBeacon(), label: 'Belt beacon' });
    const planet = buildPlanet('gas', 3, 900); // seed%3==0 → ringed
    items.push({ obj: planet, label: 'Gas planet' });
    items.push({ obj: buildPlanet('rock', 7, 700), label: 'Rock planet' });
    items.push({ obj: buildPlanet('ice', 11, 700), label: 'Ice planet' });
    const wreck = buildWreck();
    wreck.scale.setScalar(40);
    items.push({ obj: wreck, label: 'Wreck' });
    const roid = buildAsteroid(1234, 20);
    roid.scale.setScalar(28);
    items.push({ obj: roid, label: 'Asteroid' });
    layout(items, 5600);
    camera.position.set(0, 1600, 13200);
  } else {
    const factionCycle = ['Waker', 'Provenance Combine', 'Tidemark Compact', 'The Vigil', 'Unbound', 'Waker'];
    Object.keys(SHIP_CLASSES).forEach((key, i) => {
      const ship = buildShip(key, factionCycle[i % factionCycle.length]);
      // Normalize on-screen size so every hull reads
      const target = 46;
      const r = SHIP_CLASSES[key].radius;
      ship.scale.setScalar(target / Math.max(12, r));
      items.push({ obj: ship, label: key });
    });
    layout(items, 130);
    camera.position.set(0, 70, 330);
  }

  function layout(list: { obj: THREE.Group }[], spacing: number): void {
    const n = list.length;
    list.forEach((it, i) => {
      it.obj.position.x = (i - (n - 1) / 2) * spacing;
      scene.add(it.obj);
    });
  }
  camera.lookAt(0, 0, 0);

  function frame(now: number): void {
    for (const it of items) {
      it.obj.rotation.y = now * 0.0003;
      const blinkers = (it.obj.userData.blinkers as THREE.Mesh[] | undefined) ?? [];
      for (const b of blinkers) {
        (b.material as THREE.MeshBasicMaterial).opacity =
          Math.sin(now * 0.004 + ((b.userData.blinkPhase as number) ?? 0) * Math.PI * 2) > 0.2 ? 0.95 : 0.15;
      }
      it.obj.traverse((o) => {
        if (o.userData.spin) o.rotation.y += o.userData.spin as number;
      });
    }
    renderer.render(scene, camera);
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);
}
